import cx from "classnames";
import { t } from "ttag";

import { useRegisterShortcut } from "metabase/palette/hooks/useRegisterShortcut";
import { Icon, SegmentedControl } from "metabase/ui";

import QuestionDisplayToggleS from "./QuestionDisplayToggle.module.css";

export interface QuestionDisplayToggleProps {
  className?: string;
  isShowingRawTable: boolean;
  onToggleRawTable: (isShowingRawTable: boolean) => void;
}

export const QuestionDisplayToggle = ({
  className,
  isShowingRawTable,
  onToggleRawTable,
}: QuestionDisplayToggleProps) => {
  useRegisterShortcut(
    [
      {
        id: "query-builder-toggle-visualization",
        perform: () => onToggleRawTable(!isShowingRawTable),
      },
    ],
    [isShowingRawTable, onToggleRawTable],
  );

  return (
    <SegmentedControl
      classNames={{
        root: cx(QuestionDisplayToggleS.Well, className),
        label: QuestionDisplayToggleS.ToggleIcon,
        indicator: cx(
          QuestionDisplayToggleS.ToggleIcon,
          QuestionDisplayToggleS.active,
        ),
      }}
      // Driven through Mantine's own onChange instead of a root-level
      // onClick/onKeyDown + tabIndex hack — the items below used to be
      // `disabled: true`, which made their native radio inputs entirely
      // non-focusable (WCAG 2.1.1, aria_keyboard_handler_exists). Letting
      // them be real, enabled radios gives Tab/Arrow-key radiogroup
      // semantics for free.
      onChange={(value) => onToggleRawTable(value === "data")}
      value={isShowingRawTable ? "data" : "visualization"}
      data-testid="query-display-tabular-toggle"
      data={[
        {
          value: "data",
          label: (
            <Icon
              size={16}
              name="table2"
              className={cx(QuestionDisplayToggleS.InnerLabel, {
                [QuestionDisplayToggleS.activeLabel]: isShowingRawTable,
              })}
              aria-label={t`Switch to data`}
            />
          ),
        },
        {
          value: "visualization",
          label: (
            <Icon
              size={16}
              name="lineandbar"
              className={cx(QuestionDisplayToggleS.InnerLabel, {
                [QuestionDisplayToggleS.activeLabel]: !isShowingRawTable,
              })}
              aria-label={t`Switch to visualization`}
            />
          ),
        },
      ]}
    />
  );
};
